<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600 z-10">
        <th class="text-left uppercase tracking-wider bg-gray-50 w-2/12">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.contractWorks') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.budget') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showSubcontractSummary ? $t('HeadContract.ProgressClaimsShowLite.table.hideSubcontractColumns') : $t('HeadContract.ProgressClaimsShowLite.table.showSubcontractColumns')"
              @click.stop="showSubcontractSummary = !showSubcontractSummary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showSubcontractSummary}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showSubcontractSummary">{{ $t('HeadContract.ProgressClaimsShowLite.table.subcontractClaim') }}</span>
          </div>
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-green-50"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.table.currentClaim') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          <span v-if="showSubcontractSummary">{{ $t('HeadContract.ProgressClaimsShowLite.table.subcontractCertified') }}</span>
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.currentCertified') }}
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-green-50"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.table.variance') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.previous') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.cumulative') }}
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">{{ $t('HeadContract.ProgressClaimsShowLite.table.currentClaimPercentage') }}</span>
          </div>
        </th>

        <th
          v-if="!claim?.capture_claim_amount || showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          <div class="flex items-center justify-end space-x-2">
            <button
              v-if="!claim?.capture_claim_amount"
              type="button"
              class="btn-icon btn-icon-default"
              :class="[showPercentages ? 'mr-auto' : 'mx-auto']"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 transition-all duration-200"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">{{ $t('HeadContract.ProgressClaimsShowLite.table.currentCertifiedPercentage') }}</span>
          </div>
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.table.previousPercentage') }}
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50 pr-3"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.table.cumulativePercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.costToComplete') }}
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <TradeRow
        v-for="trade in claim.trades"
        :key="trade.id"
        :trade="trade"
        :claim="claim"
        :show-percentages="showPercentages"
        :show-subcontract-summary="showSubcontractSummary"
        :lump-sum-cost-ids-for-claim-period="props.lumpSumCostIdsForClaimPeriod"
        :rates-cost-ids-for-claim-period="props.ratesCostIdsForClaimPeriod"
      />

      <!-- Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.subtotal') }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          <div>
            <span
              v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0"
              class="text-gray-600"
            >Works:</span> {{ $tcc(totalCosts.trades.budget) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span
              v-if="totalCosts.trades.budget !== 0"
              class="text-gray-600"
            >Adjustments:</span> {{ $tcc(-(totalCosts.trades.budget - totalCosts.trades.adjustedBudget)) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span class="text-gray-600">Sum:</span> {{ $tcc(totalCosts.trades.adjustedBudget) }}
          </div>
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.trades.subcontract_claim) }}</span>
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold text-gray-900"
        >
          {{ $tcc(totalCosts.trades.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.trades.subcontract_certified) }}</span>
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.trades.current_certified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.trades.variance !== 0, 'text-gray-900': totalCosts.trades.variance === 0 }"
        >
          {{ $tcc(totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold text-gray-900"
        >
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_claim / totalCosts.trades.budget) ? $tn((totalCosts.trades.current_claim / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="!claim.capture_claim_amount || showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_certified / totalCosts.trades.budget) ? $tn((totalCosts.trades.current_certified / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.previous / totalCosts.trades.budget) ? $tn((totalCosts.trades.previous / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ isNumber(totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) ? $tn((totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(Math.abs(totalCosts.trades.budget - totalCosts.trades.cumulativeCertified)) }}
        </td>
      </tr>

      <template
        v-for="(orders, key) in variationOrders"
        :key="key"
      >
        <VariationOrderRow
          :claim="claim"
          :variation-orders="orders"
          :title="VariationOrderTitles[key]"
          :variation-order-type="VariationOrderType[key]"
          :show-percentages="showPercentages"
          :show-subcontract-summary="showSubcontractSummary"
          :variation-order-cost-ids-for-claim-period="props.variationOrderCostIdsForClaimPeriod"
        />
      </template>

      <!-- Variation Orders Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.subtotal') }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.budget) }}
        </td>

        <td class="text-right font-semibold text-gray-900" />

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold text-gray-900"
        >
          {{ $tcc(totalCosts.variationOrders.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900" />

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.current_certified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance !== 0, 'text-gray-900': totalCosts.variationOrders.variance === 0 }"
        >
          {{ $tcc(totalCosts.variationOrders.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-semibold text-gray-900"
        >
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.current_claim / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="!claim.capture_claim_amount || showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.current_certified / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.previous / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.cumulativeCertified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified) }}
        </td>
      </tr>

      <SharedFundRow
        v-if="sharedFunds.length && claim"
        :shared-funds="sharedFunds"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Grand Total Row -->
      <tr class="child:py-4 first-child:pl-3 last-child:pr-3 bg-blue-50 child:border-t-2 child:border-blue-200 sticky bottom-0">
        <td class="font-bold text-blue-900">
          {{ $t('HeadContract.ProgressClaimsShowLite.table.grandTotal') }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.budget + totalCosts.trades.adjustedBudget + totalCosts.sharedFund.budget) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.trades.subcontract_claim) }}</span>
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold text-blue-900"
        >
          {{ $tcc(totalCosts.variationOrders.current_claim + totalCosts.trades.current_claim + totalCosts.sharedFund.current_claim) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.trades.subcontract_certified) }}</span>
        </td>

        <td class="text-right font-bold text-blue-900">
          <p>
            {{ $tcc(totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified + totalCosts.sharedFund.current_certified) }}
          </p>

          <p class="text-gray-500">
            Payable: {{ $tcc(claim.totalPayable) }}
          </p>
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance + totalCosts.trades.variance !== 0, 'text-blue-900': totalCosts.variationOrders.variance + totalCosts.trades.variance === 0 }"
        >
          {{ $tcc(totalCosts.variationOrders.variance + totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(totalCosts.variationOrders.previous + totalCosts.trades.previous + totalCosts.sharedFund.previous) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified + totalCosts.sharedFund.cumulativeCertified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold text-blue-900"
        >
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? $tn(((totalCosts.variationOrders.current_claim + totalCosts.trades.current_claim) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="!claim.capture_claim_amount || showPercentages"
          class="text-right font-bold text-blue-900"
        >
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? $tn(((totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? $tn(((totalCosts.variationOrders.previous + totalCosts.trades.previous) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? $tn(((totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.budget - totalCosts.trades.cumulativeCertified + totalCosts.sharedFund.budget - totalCosts.sharedFund.cumulativeCertified) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { australianNumber, compareCodeRefs, isNumber } from '@/helpers';
import SharedFund from '@/models/SharedFund';
import VariationOrder, {
  VariationOrderStatus,
  VariationOrderTitles,
  VariationOrderType,
} from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { Collection } from 'pinia-orm';
import { computed, ref } from 'vue';
import SharedFundRow from './SharedFundRow.vue';
import TradeRow from './TradeRow.vue';
import VariationOrderRow from './VariationOrderRow.vue';

const props = defineProps<{
  claimId: string;
  projectId: string;
  lumpSumCostIdsForClaimPeriod: Id[];
  ratesCostIdsForClaimPeriod: Id[];
  variationOrderCostIdsForClaimPeriod: Id[];
}>();

const claimsStore = useClaimsStore();
const projectsStore = useProjectsStore();
const sharedFundsStore = useSharedFundsStore();
const showPercentages = ref(false);
const showSubcontractSummary = ref(false);

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items', (query) => {
          query.whereNull('parent_id');
        })
          .where('boq_type', 'direct')
          .orderBy('sort_order');
      })
      .with('trade_item_costs')
      .with('variation_orders', (query) => {
        query.whereIn('status', [
          VariationOrderStatus.approved,
          VariationOrderStatus.principled,
          VariationOrderStatus.completed,
        ])
          .where((variationOrder) => !variationOrder.isTransferred)
          .with('trade_items');
      })
      .with('variation_order_costs')
      .with('shared_fund_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      adjustedBudget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
      subcontract_claim: 0,
      subcontract_certified: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
      subcontract_claim: 0,
      subcontract_certified: 0,
    },

    sharedFund: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },
  };

  if(claim.value) {
    claim.value.shared_fund_costs?.forEach((sharedFundCost) => {
      totals.sharedFund.current_claim += sharedFundCost.current_claim || 0;
      totals.sharedFund.current_certified += sharedFundCost.current_certified || 0;
      totals.sharedFund.variance += (sharedFundCost.current_claim || 0) - sharedFundCost.current_certified;
    });

    if(sharedFunds.value.length > 0 && claim.value) {
      sharedFunds.value.forEach((fund) => {
        totals.sharedFund.budget += fund.budget;
        totals.sharedFund.previous += fund.previous(claim.value);
      });
    }

    claim.value.trades?.forEach((trade) => {
      trade.trade_items.forEach((tradeItem) => {
        totals.trades.budget += tradeItem.childBudget();
        totals.trades.adjustedBudget += tradeItem.childAdjustedBudget();
        totals.trades.previous += tradeItem.childPrevious(claim.value);

        totals.trades.subcontract_claim += tradeItem.childSubcontractSummaryClaim(
          props.lumpSumCostIdsForClaimPeriod,
          props.ratesCostIdsForClaimPeriod,
        );

        totals.trades.subcontract_certified += tradeItem.childSubcontractSummaryCertified(
          props.lumpSumCostIdsForClaimPeriod,
          props.ratesCostIdsForClaimPeriod,
        );
      });
    });

    claim.value.trade_item_costs.forEach((cost) => {
      totals.trades.current_claim += cost.current_claim || 0;
      totals.trades.current_certified += cost.current_certified;
      totals.trades.variance += cost.current_claim - cost.current_certified;
    });

    claim.value.variation_orders?.forEach((variationOrder) => {
      totals.variationOrders.budget += variationOrder.totalBudget;
      totals.variationOrders.previous += variationOrder.previous(claim.value);

      totals.variationOrders.subcontract_claim += variationOrder.subcontractSummaryClaim(
        props.variationOrderCostIdsForClaimPeriod,
      );

      totals.variationOrders.subcontract_certified += variationOrder.subcontractSummaryCertified(
        props.variationOrderCostIdsForClaimPeriod,
      );
    });

    claim.value.variation_order_costs?.forEach((variationOrderCost) => {
      totals.variationOrders.current_claim += variationOrderCost.current_claim || 0;
      totals.variationOrders.current_certified += variationOrderCost.current_certified;
      totals.variationOrders.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  totals.sharedFund.cumulativeCertified = totals.sharedFund.current_certified + totals.sharedFund.previous;

  return totals;
});

const sharedFunds = computed(() => {
  if(!projectsStore.project?.project_group_id) {
    return [] as SharedFund[];
  }

  return sharedFundsStore.models
    .with('costs')
    .with('project_group', (query) => query.with('projects'))
    .get();
});

const variationOrdersStore = useVariationOrdersStore();

const variationOrders = computed(() => {
  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrder> }> = {};

  for(const key in VariationOrderType) {
    const variationOrders = variationOrdersStore.models
      .where('project_id', parseInt(props.projectId))
      .whereNull('package_id')
      .whereIn(
        'status',
        [VariationOrderStatus.approved, VariationOrderStatus.principled, VariationOrderStatus.completed],
      )
      .where((variationOrder) => !variationOrder.isTransferred)
      .where(
        'type',
        VariationOrderType[key],
      ).with('costs')
      .with('trade_items')
      .with('head_contract_trade_item')
      .get();

    // Transferred variation orders that have a cost on this claim
    const historicalVariationOrders = variationOrdersStore.models
      .where('project_id', parseInt(props.projectId))
      .whereNull('package_id')
      .where((variationOrder) => {
        return variationOrder.isTransferred;
      })
      .whereHas('costs', (query) => {
        query.where('claim_id', parseInt(props.claimId));
      })
      .where('type', VariationOrderType[key])
      .with('trade_items')
      .with('head_contract_trade_item')
      .get();

    costs[key] = variationOrders
      .concat(historicalVariationOrders)
      .sort((a, b) => compareCodeRefs(a.CodeRef || '', b.CodeRef || ''));
  }

  return costs;
});
</script>

<style scoped></style>
